/**
 * TextResponse — single-line input OR multi-line textarea.
 *
 * Multi-line (default) submits on Enter (Shift+Enter for newline). Single-
 * line submits on Enter only. Both fire `onSubmit` with the trimmed string;
 * empty input is rejected at the component boundary.
 */

import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import type { ResponseCoreProps } from './types';
import './responses.css';

interface Props extends ResponseCoreProps<string> {
  /** Render a `<textarea>` instead of `<input>`. Default: true. */
  multiline?: boolean;
  /** Number of visible text lines when multiline. Default: 2. */
  rows?: number;
  /** Input mode hint for on-screen keyboards. */
  inputMode?: 'text' | 'email' | 'tel' | 'url' | 'search' | 'numeric' | 'decimal';
  /** Autocomplete attribute — e.g. "email", "tel", "postal-code". */
  autoComplete?: string;
  /** Max character length. */
  maxLength?: number;
  /** Placeholder shown when empty. */
  placeholder?: string;
}

export function TextResponse({
  label,
  hideLabel,
  initialValue = '',
  onChange,
  onSubmit,
  disabled,
  autoFocus,
  hideSubmit,
  submitLabel = 'Send',
  helper,
  multiline = true,
  rows = 2,
  inputMode,
  autoComplete,
  maxLength,
  placeholder,
}: Props) {
  const [value, setValue] = useState<string>(initialValue ?? '');
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  function update(next: string) {
    setValue(next);
    onChange?.(next);
  }

  function commit(e?: FormEvent) {
    if (e) e.preventDefault();
    const trimmed = value.trim();
    if (trimmed.length === 0) return;
    onSubmit?.(trimmed);
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      commit();
    }
  }

  const id = useUniqueId('cf-text');
  const labelEl = hideLabel ? (
    <label htmlFor={id} className="cf-visually-hidden">
      {label}
    </label>
  ) : (
    <label htmlFor={id} className="cf-response__label">
      {label}
    </label>
  );

  return (
    <form className="cf-response cf-text-response" onSubmit={commit}>
      {labelEl}
      {multiline ? (
        <textarea
          ref={inputRef as React.RefObject<HTMLTextAreaElement>}
          id={id}
          className="cf-input cf-text-response__textarea"
          rows={rows}
          value={value}
          placeholder={placeholder}
          inputMode={inputMode}
          autoComplete={autoComplete}
          maxLength={maxLength}
          disabled={disabled}
          onChange={(e) => update(e.target.value)}
          onKeyDown={onKeyDown}
        />
      ) : (
        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          id={id}
          type="text"
          className="cf-input"
          value={value}
          placeholder={placeholder}
          inputMode={inputMode}
          autoComplete={autoComplete}
          maxLength={maxLength}
          disabled={disabled}
          onChange={(e) => update(e.target.value)}
          onKeyDown={onKeyDown}
        />
      )}
      {helper && <div className="cf-response__helper">{helper}</div>}
      {!hideSubmit && (
        <div className="cf-response__actions">
          <button
            type="submit"
            className="cf-btn cf-btn--primary"
            disabled={disabled || value.trim().length === 0}
          >
            {submitLabel}
          </button>
        </div>
      )}
    </form>
  );
}

// ---------------------------------------------------------------------------
// Tiny id helper — avoids a useId polyfill in test env.
// ---------------------------------------------------------------------------

let uid = 0;
function useUniqueId(prefix: string): string {
  const [id] = useState(() => `${prefix}-${++uid}`);
  return id;
}
